import { SocialPlatform } from '../types/profile.types';
import { SvgIcon } from '@/types';

// Import SVG icons
import IcInstagram from '@/assets/icons/integrations/instagram.svg';
import IcTiktok from '@/assets/icons/ic-tiktok.svg';
import IcMail from '@/assets/icons/mail-to.svg';
import IcFacebook from '@/assets/icons/socials/facebook.svg';
import IcYoutube from '@/assets/icons/ic-youtube.svg';
import IcLink from '@/assets/icons/socials/link-with-bg.svg';
import IcPinterest from '@/assets/icons/ic-pinterest.svg';
import IcLinkedin from '@/assets/icons/socials/linkedin.svg';
import IcX from '@/assets/icons/ic-x.svg';
import IcSpotify from '@/assets/icons/ic-spotify.svg';
import IcApplePodcast from '@/assets/icons/ic-apple-podcast.svg';
import IcEtsy from '@/assets/icons/ic-etsy.svg';
import IcDiscord from '@/assets/icons/ic-discord.svg';
import IcSnapchat from '@/assets/icons/socials/snapchat.svg';
import IcTwitch from '@/assets/icons/ic-twitch.svg';
import IcVimeo from '@/assets/icons/ic-vimeo.svg';

export type SocialInputType = 'username' | 'url';

export interface SocialPlatformConfig {
    platform: SocialPlatform;
    label: string;
    placeholder: string;
    inputType: SocialInputType;
    hintLabel: string; // The hint text shown in the input (e.g., '@', 'URL', 'Email')
    baseUrl?: string; // For username inputs, the base URL (Note: username-type platforms store username only, not full URL)
    validation?: {
        minLength?: number;
        maxLength?: number;
        pattern?: RegExp;
    };
}

export const SOCIAL_PLATFORM_CONFIGS: SocialPlatformConfig[] = [
    {
        platform: 'instagram',
        label: 'Instagram',
        placeholder: 'Your Username',
        inputType: 'username',
        hintLabel: '@',
        baseUrl: 'https://instagram.com/', // Display only - username-type platforms store username, not URL
        validation: {
            minLength: 1,
            maxLength: 30,
            pattern: /^[a-zA-Z0-9._]+$/,
        },
    },
    {
        platform: 'tiktok',
        label: 'TikTok',
        placeholder: 'Your Username',
        inputType: 'username',
        hintLabel: '@',
        baseUrl: 'https://tiktok.com/@', // Display only - username-type platforms store username, not URL
        validation: {
            minLength: 1,
            maxLength: 24,
            pattern: /^[a-zA-Z0-9._]+$/,
        },
    },
    {
        platform: 'email',
        label: 'Email',
        placeholder: 'email@gmail.com',
        inputType: 'url',
        hintLabel: 'Email',
        validation: {
            pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        },
    },
    {
        platform: 'facebook',
        label: 'Facebook',
        placeholder: 'https://www.facebook.com/',
        inputType: 'url',
        hintLabel: 'URL',
        validation: {
            pattern: /^https?:\/\/(www\.)?facebook\.com\/.+$/,
        },
    },
    {
        platform: 'youtube',
        label: 'YouTube',
        placeholder: 'https://youtube.com/',
        inputType: 'url',
        hintLabel: 'URL',
        validation: {
            pattern: /^https?:\/\/(www\.)?(youtube\.com|youtu\.be)\/.+$/,
        },
    },
    {
        platform: 'link',
        label: 'URL',
        placeholder: 'https://link.com/',
        inputType: 'url',
        hintLabel: 'URL',
        validation: {
            pattern: /^https?:\/\/.+$/,
        },
    },
    {
        platform: 'pinterest',
        label: 'Pinterest',
        placeholder: 'https://www.pinterest.com/',
        inputType: 'url',
        hintLabel: 'URL',
        validation: {
            pattern: /^https?:\/\/(www\.)?pinterest\.com\/.+$/,
        },
    },
    {
        platform: 'linkedin',
        label: 'LinkedIn',
        placeholder: 'https://www.linkedin.com/',
        inputType: 'url',
        hintLabel: 'URL',
        validation: {
            pattern: /^https?:\/\/(www\.)?linkedin\.com\/in\/.+$/,
        },
    },
    {
        platform: 'twitter',
        label: 'X (Twitter)',
        placeholder: 'Your Username',
        inputType: 'username',
        hintLabel: '@',
        baseUrl: 'https://twitter.com/', // Display only - username-type platforms store username, not URL
        validation: {
            minLength: 1,
            maxLength: 15,
            pattern: /^[a-zA-Z0-9_]+$/,
        },
    },
    {
        platform: 'spotify',
        label: 'Spotify',
        placeholder: 'https://open.spotify.com/',
        inputType: 'url',
        hintLabel: 'URL',
        validation: {
            pattern: /^https?:\/\/open\.spotify\.com\/.+$/,
        },
    },
    {
        platform: 'apple-podcasts',
        label: 'Apple Podcasts',
        placeholder: 'https://podcasts.apple.com/',
        inputType: 'url',
        hintLabel: 'URL',
        validation: {
            pattern: /^https?:\/\/podcasts\.apple\.com\/.+$/,
        },
    },
    {
        platform: 'etsy',
        label: 'Etsy',
        placeholder: 'https://etsy.com/',
        inputType: 'url',
        hintLabel: 'URL',
        validation: {
            pattern: /^https?:\/\/(www\.)?etsy\.com\/.+$/,
        },
    },
    {
        platform: 'discord',
        label: 'Discord',
        placeholder: 'https://www.discord.com/',
        inputType: 'url',
        hintLabel: 'URL',
        validation: {
            pattern: /^https?:\/\/(www\.)?(discord\.gg|discord\.com\/invite)\/.+$/,
        },
    },
    {
        platform: 'snapchat',
        label: 'Snapchat',
        placeholder: 'https://snapchat.com/',
        inputType: 'url',
        hintLabel: 'URL',
        validation: {
            pattern: /^https?:\/\/(www\.)?snapchat\.com\/.+$/,
        },
    },
    {
        platform: 'twitch',
        label: 'Twitch',
        placeholder: 'https://www.twitch.tv/',
        inputType: 'url',
        hintLabel: 'URL',
        baseUrl: 'https://twitch.tv/',
        validation: {
            minLength: 4,
            maxLength: 25,
            pattern: /^[a-zA-Z0-9_]+$/,
        },
    },
    {
        platform: 'vimeo',
        label: 'Vimeo',
        placeholder: 'https://vimeo.com/',
        inputType: 'url',
        hintLabel: 'URL',
        validation: {
            pattern: /^https?:\/\/(www\.)?vimeo\.com\/.+$/,
        },
    },
];

// Helper function to get config for a platform
export const getSocialPlatformConfig = (platform: SocialPlatform): SocialPlatformConfig | undefined => {
    return SOCIAL_PLATFORM_CONFIGS.find(config => config.platform === platform);
};

// Helper function to construct full URL from username
export const constructSocialUrl = (platform: SocialPlatform, input: string): string => {
    const config = getSocialPlatformConfig(platform);
    if (!config || config.inputType !== 'username' || !config.baseUrl) {
        return input; // Return as-is for URL inputs or platforms without baseUrl
    }

    // Remove @ symbol if present
    const cleanInput = input.replace(/^@/, '');
    return `${config.baseUrl}${cleanInput}`;
};

// Helper function to extract username from full URL
export const extractUsernameFromUrl = (platform: SocialPlatform, url: string): string => {
    const config = getSocialPlatformConfig(platform);
    if (!config || !config.baseUrl) {
        return url;
    }

    if (url.startsWith(config.baseUrl)) {
        return url.replace(config.baseUrl, '').replace(/^@/, '');
    }

    return url;
};

// Platform icons mapping - Raw SVG components
export const PLATFORM_ICONS: Record<SocialPlatform, SvgIcon | string> = {
    instagram: IcInstagram,
    tiktok: IcTiktok,
    email: IcMail,
    facebook: IcFacebook,
    youtube: IcYoutube,
    link: IcLink,
    pinterest: IcPinterest,
    twitter: IcX,
    spotify: IcSpotify,
    'apple-podcasts': IcApplePodcast,
    etsy: IcEtsy,
    discord: IcDiscord,
    snapchat: IcSnapchat,
    twitch: IcTwitch,
    vimeo: IcVimeo,
    linkedin: IcLinkedin,
};
